/**
 * TransferModal Component
 * Modal for sending WiTon to another user (no fee)
 * With autocomplete user search dropdown
 */

import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  witonBalance: number;
  onTransferSuccess: () => void;
}

interface UserSuggestion {
  username: string | null;
  firstName: string;
  displayName: string;
}

export function TransferModal({ isOpen, onClose, witonBalance, onTransferSuccess }: TransferModalProps) {
  const { t } = useTranslation();
  const [recipientUsername, setRecipientUsername] = useState('');
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    const query = recipientUsername.trim().replace('@', '');
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await api.searchUsersForTransfer(query);
        setSuggestions(res.data.users || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [recipientUsername]);

  if (!isOpen) return null;

  const selectUser = (user: UserSuggestion) => {
    setRecipientUsername(user.username ? `@${user.username}` : user.firstName);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleTransfer = async () => {
    setError('');
    setSuccess('');

    const numAmount = parseFloat(amount);
    if (!recipientUsername.trim()) {
      setError(t('transfer.enter_recipient') || 'Введите username получателя');
      return;
    }
    if (isNaN(numAmount) || numAmount <= 0) {
      setError(t('transfer.enter_amount') || 'Введите корректную сумму');
      return;
    }
    if (numAmount > witonBalance) {
      setError(t('transfer.insufficient_balance') || 'Недостаточно средств');
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.sendTransfer(recipientUsername.trim(), numAmount);
      if (res.data.success) {
        setSuccess(t('transfer.success') || `✅ Перевод отправлен!`);
        onTransferSuccess();
        setTimeout(() => {
          onClose();
          setRecipientUsername('');
          setAmount('');
          setSuccess('');
        }, 1500);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Transfer failed';
      if (msg === 'BATTERY_REQUIRED') {
        setError(t('transfer.battery_required') || 'Для переводов необходима активная батарея. Купите батарею, чтобы разблокировать переводы.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)', borderRadius: '20px', padding: '24px', maxWidth: '400px', width: '100%', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
            📤 {t('transfer.title') || 'Перевод WiTon'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {/* Info */}
        <div style={{ background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', padding: '10px', marginBottom: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
          {t('transfer.no_fee_info') || 'Переводы без комиссии. Получатель будет уведомлён.'}
          <div style={{ marginTop: '4px', color: '#f59e0b', fontSize: '11px' }}>
            Max: 500,000 WiTon / day
          </div>
        </div>

        {/* Recipient with autocomplete */}
        <div style={{ marginBottom: '12px', position: 'relative' }}>
          <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px', display: 'block' }}>
            {t('transfer.recipient') || 'Получатель (username)'}
          </label>
          <input
            ref={inputRef}
            type="text"
            value={recipientUsername}
            onChange={(e) => setRecipientUsername(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            placeholder="@username"
            autoComplete="off"
            style={{ width: '100%', padding: '12px', background: 'rgba(10, 22, 40, 0.6)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
          />
          {isSearching && (
            <div style={{ position: 'absolute', right: '12px', top: '34px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>...</div>
          )}

          {/* Dropdown suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              background: '#1a2332',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '10px',
              overflow: 'hidden',
              zIndex: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}>
              {suggestions.map((user, i) => (
                <div
                  key={i}
                  onClick={() => selectUser(user)}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    borderBottom: i < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>
                    👤
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
                      {user.displayName}
                    </div>
                    {user.username && user.firstName && (
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                        {user.firstName}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Amount */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px', display: 'block' }}>
            {t('transfer.amount') || 'Сумма WiTon'}
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            style={{ width: '100%', padding: '12px', background: 'rgba(10, 22, 40, 0.6)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '10px', color: '#fff', fontSize: '18px', fontWeight: 600, outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            {t('user.balance') || 'Баланс'}: {witonBalance.toLocaleString()} WiTon
          </div>
        </div>

        {/* Error/Success */}
        {error && (
          <div style={{ background: 'rgba(239, 68, 68, 0.15)', borderRadius: '8px', padding: '10px', marginBottom: '12px', color: '#ef4444', fontSize: '13px' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: 'rgba(16, 185, 129, 0.15)', borderRadius: '8px', padding: '10px', marginBottom: '12px', color: '#10b981', fontSize: '13px' }}>
            {success}
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleTransfer}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '14px',
            background: isLoading ? 'rgba(59,130,246,0.3)' : 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 700,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: isLoading ? 'none' : '0 4px 20px rgba(123, 31, 162, 0.4)',
          }}
        >
          {isLoading ? '...' : (t('transfer.send_button') || 'Отправить')}
        </button>
      </div>
    </div>
  );
}
